import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {Search, MapPin, Briefcase, Building2, Clock, X, Filter} from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useVacancies, type Vacancy } from '@/hooks/useVacancies';
import { useAuth } from '@/hooks/useAuth';
import { lumi } from '@/lib/lumi';
import { INDUSTRIES } from '@/lib/industries';

const modalities = ['Todas', 'Presencial', 'Remota', 'Hibrida'];

const toTitleCase = (s: string) =>
s.
trim().
toLowerCase().
replace(/\s+/g, ' ').
split(' ').
map((w) => w ? w.charAt(0).toUpperCase() + w.slice(1) : w).
join(' ');

function Empleos() {
  const { vacancies, loading, fetchPublic } = useVacancies();
  const { user, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();

  const [query, setQuery] = useState('');
  const [city, setCity] = useState('');
  const [modality, setModality] = useState('Todas');
  const [industry, setIndustry] = useState('');
  const [minSalary, setMinSalary] = useState('');
  const [selected, setSelected] = useState<Vacancy | null>(null);
  const [applying, setApplying] = useState(false);
  const [appliedIds, setAppliedIds] = useState<string[]>([]);

  useEffect(() => {
    if (isAuthenticated) fetchPublic();
  }, [isAuthenticated, fetchPublic]);

  useEffect(() => {
    if (user) {
      lumi.entities.applications
        .list({ filter: { collaboratorUserId: user.userId } })
        .then((r) => setAppliedIds(r.list.map((a: any) => a.vacancyId)))
        .catch((e) => console.error('Error al cargar postulaciones:', e));
    } else {
      setAppliedIds([]);
    }
  }, [user]);

  const industries = useMemo(() => {
    const set = new Set<string>(INDUSTRIES.filter((i) => i !== 'Otro'));
    vacancies.forEach((v) => v.industry && set.add(v.industry));
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'es'));
  }, [vacancies]);

  const filtered = useMemo(() => {
    return vacancies.filter((v) => {
      if (query) {
        const q = query.toLowerCase();
        const match =
        v.title?.toLowerCase().includes(q) ||
        v.companyName?.toLowerCase().includes(q) ||
        v.area?.toLowerCase().includes(q);
        if (!match) return false;
      }
      if (city && !v.city?.toLowerCase().includes(city.toLowerCase())) return false;
      if (modality !== 'Todas' && v.modality !== modality) return false;
      if (industry && v.industry !== industry) return false;
      if (minSalary && (v.salaryMax || v.salaryMin || 0) < Number(minSalary)) return false;
      return true;
    });
  }, [vacancies, query, city, modality, industry, minSalary]);

  const fmtSalary = (v: Vacancy) => {
    if (v.salaryMin && v.salaryMax) return `$${v.salaryMin.toLocaleString()} - $${v.salaryMax.toLocaleString()}`;
    if (v.salaryMin) return `Desde $${v.salaryMin.toLocaleString()}`;
    return 'A convenir';
  };

  const handleSelectVacancy = (v: Vacancy) => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/empleos' } });
      return;
    }
    setSelected(v);
  };

  const handleApply = async (v: Vacancy) => {
    if (!isAuthenticated || !user) {
      navigate('/login', { state: { from: '/empleos' } });
      return;
    }
    if (appliedIds.includes(v._id)) return;
    setApplying(true);
    try {
      const colRes = await lumi.entities.collaborators.list({ filter: { userId: user.userId }, limit: 1 });
      const profile = colRes.list[0];
      const now = new Date().toISOString();
      await lumi.entities.applications.create({
        vacancyId: v._id,
        vacancyTitle: v.title,
        companyUserId: v.companyUserId,
        companyName: v.companyName,
        collaboratorUserId: user.userId,
        collaboratorName: profile?.fullName || user.email,
        collaboratorEmail: user.email,
        cvUrl: profile?.cvUrl || '',
        coverNote: '',
        status: 'pendiente',
        creator: user.userId,
        createdAt: now,
        updatedAt: now
      });
      await lumi.entities.vacancies.update(v._id, {
        applicantsCount: (v.applicantsCount || 0) + 1,
        updatedAt: now
      });
      setAppliedIds((p) => [...p, v._id]);
      window.alert('¡Postulación enviada con éxito!');
    } catch (e) {
      console.error('Error al postularse:', e);
      window.alert('Ocurrió un error al postularte. Inténtalo de nuevo.');
    } finally {
      setApplying(false);
    }
  };

  const inputCls =
  'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-amber-400 focus:ring-1 focus:ring-amber-400';

  // --- Pantalla de acceso requerido ---
  if (isLoading) {
    return (
      <div className="flex min-h-screen flex-col bg-slate-50">
        <Navbar />
        <div className="flex flex-1 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-amber-400 border-t-transparent" />
        </div>
        <Footer />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="flex min-h-screen flex-col bg-slate-50">
        <Navbar />
        <div className="flex flex-1 flex-col items-center justify-center gap-6 px-4 py-20 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-amber-50">
            <Briefcase size={32} className="text-amber-500" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Inicia sesión para ver las vacantes</h2>
            <p className="mt-2 text-sm text-slate-500">
              Necesitas una cuenta en Beeznz Work para explorar y postularte a las vacantes disponibles.
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <button
              onClick={() => navigate('/login', { state: { from: '/empleos' } })}
              className="rounded-lg bg-slate-900 px-6 py-3 text-sm font-semibold text-amber-400 transition hover:bg-slate-800"
            >
              Iniciar sesión
            </button>
            <button
              onClick={() => navigate('/registro', { state: { from: '/empleos' } })}
              className="rounded-lg border border-slate-300 bg-white px-6 py-3 text-sm font-semibold text-slate-700 transition hover:border-amber-400 hover:text-amber-600"
            >
              Crear cuenta gratis
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <Navbar />

      <section className="bg-slate-900 px-4 py-12 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <h1 className="text-2xl font-bold text-white sm:text-3xl">Encuentra tu próximo empleo</h1>
          <p className="mt-1.5 text-sm text-slate-300">Explora vacantes de empresas verificadas en Beeznz Work</p>
          <div className="mt-5 flex items-center gap-2 rounded-xl bg-white p-2 shadow-lg">
            <Search size={18} className="ml-2 shrink-0 text-slate-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Puesto, empresa o área..."
              className="flex-1 bg-transparent px-1 py-1.5 text-sm text-slate-900 outline-none" />

          </div>
        </div>
      </section>

      <main className="mx-auto flex w-full max-w-7xl flex-1 gap-6 px-4 py-8 sm:px-6 lg:px-8">
        {/* Filters */}
        <aside className="hidden w-64 shrink-0 lg:block">
          <div className="sticky top-20 rounded-xl border border-slate-200 bg-white p-5">
            <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-900">
              <Filter size={15} /> Filtros
            </div>
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">Ciudad</label>
                <input className={inputCls} value={city} onChange={(e) => setCity(e.target.value)} placeholder="Ej. Puebla" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">Modalidad</label>
                <select className={inputCls} value={modality} onChange={(e) => setModality(e.target.value)}>
                  {modalities.map((m) => <option key={m} value={m}>{m === 'Hibrida' ? 'Híbrida' : m}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">Giro</label>
                <select className={inputCls} value={industry} onChange={(e) => setIndustry(e.target.value)}>
                  <option value="">Todos</option>
                  {industries.map((i) => <option key={i} value={i}>{i}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">Salario mínimo</label>
                <input type="number" className={inputCls} value={minSalary} onChange={(e) => setMinSalary(e.target.value)} placeholder="$" />
              </div>
            </div>
          </div>
        </aside>

        {/* Results */}
        <div className="flex-1">
          <p className="mb-4 text-sm text-slate-500">
            {loading ? 'Cargando...' : `${filtered.length} vacante${filtered.length !== 1 ? 's' : ''} encontrada${filtered.length !== 1 ? 's' : ''}`}
          </p>

          {!loading && filtered.length === 0 ?
          <div className="rounded-xl border border-dashed border-slate-300 bg-white py-16 text-center">
              <Briefcase size={28} className="mx-auto text-slate-300" />
              <p className="mt-3 font-semibold text-slate-700">No hay vacantes con estos filtros</p>
              <p className="mt-1 text-sm text-slate-500">Ajusta tu búsqueda para ver más resultados.</p>
            </div> :

          <div className="grid gap-4">
              {filtered.map((v) =>
            <button
              key={v._id}
              onClick={() => handleSelectVacancy(v)}
              className="rounded-xl border border-slate-200 bg-white p-5 text-left transition hover:border-amber-300 hover:shadow-sm">

                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="font-bold text-slate-900">{v.title}</h3>
                      <p className="mt-0.5 flex items-center gap-1.5 text-sm text-slate-500">
                        <Building2 size={13} /> {v.companyName || 'Empresa'}
                      </p>
                    </div>
                    {appliedIds.includes(v._id) &&
                <span className="shrink-0 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">Postulado</span>
                }
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-xs text-slate-500">
                    <span className="inline-flex items-center gap-1"><MapPin size={13} /> {v.city || '—'}</span>
                    <span className="inline-flex items-center gap-1"><Clock size={13} /> {v.modality}</span>
                    <span className="inline-flex items-center gap-1 font-semibold text-amber-600"><Briefcase size={13} /> {fmtSalary(v)}</span>
                  </div>
                </button>
            )}
            </div>
          }
        </div>
      </main>

      {/* Detail drawer */}
      {selected &&
      <div className="fixed inset-0 z-50 flex justify-end bg-black/50" onClick={() => setSelected(null)}>
          <div className="flex h-full w-full max-w-lg flex-col bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
              <h2 className="text-lg font-bold text-slate-900">Detalle de vacante</h2>
              <button onClick={() => setSelected(null)} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100">
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-5">
              <h3 className="text-xl font-bold text-slate-900">{selected.title}</h3>
              <p className="mt-1 flex items-center gap-1.5 text-sm text-slate-500">
                <Building2 size={14} /> {selected.companyName} · {selected.area}
              </p>
              <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                <Info label="Ubicación" value={`${selected.city || '—'}${selected.state ? `, ${selected.state}` : ''}`} />
                <Info label="Modalidad" value={selected.modality} />
                <Info label="Salario" value={fmtSalary(selected)} />
                <Info label="Contratación" value={selected.contractType || '—'} />
                <Info label="Horario" value={selected.schedule || '—'} />
                <Info label="Experiencia" value={selected.experience || '—'} />
              </div>
              {selected.skills && selected.skills.length > 0 &&
            <div className="mt-4">
                  <p className="mb-2 text-xs font-semibold text-slate-600">Habilidades requeridas</p>
                  <div className="flex flex-wrap gap-2">
                    {selected.skills.map((s) =>
                <span key={s} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">{toTitleCase(s)}</span>
                )}
                  </div>
                </div>
            }
              <Section title="Actividades a desempeñar" value={selected.activities} />
              <Section title="Requisitos" value={selected.requirements} />
              <Section title="Prestaciones" value={selected.benefits} />
            </div>
            <div className="border-t border-slate-100 px-6 py-4">
              <button
              onClick={() => handleApply(selected)}
              disabled={applying || appliedIds.includes(selected._id)}
              className="w-full rounded-lg bg-slate-900 px-5 py-3 text-sm font-semibold text-amber-400 transition hover:bg-slate-800 disabled:opacity-60">

                {appliedIds.includes(selected._id) ? 'Ya te postulaste' : applying ? 'Enviando...' : 'Postularme a esta vacante'}
              </button>
            </div>
          </div>
        </div>
      }

      <Footer />
    </div>);

}

function Info({ label, value }: {label: string;value: string;}) {
  return (
    <div className="rounded-lg bg-slate-50 p-3">
      <p className="text-xs text-slate-400">{label}</p>
      <p className="mt-0.5 font-semibold text-slate-800">{value}</p>
    </div>);

}

function Section({ title, value }: {title: string;value?: string;}) {
  if (!value) return null;
  return (
    <div className="mt-4">
      <p className="mb-1 text-xs font-semibold text-slate-600">{title}</p>
      <p className="whitespace-pre-line text-sm leading-relaxed text-slate-600">{value}</p>
    </div>);

}

export default Empleos;